import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { TESTIDS } from "@/constants/testIds";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Building2, MapPin, Pencil, Trash2 } from "lucide-react";

const emptyForm = { name: "", lat: -6.2088, lng: 106.8456, radius_meters: 100, timezone_offset: 7 };
const TZ_LABELS = { 7: "WIB (UTC+7)", 8: "WITA (UTC+8)", 9: "WIT (UTC+9)" };

export default function AdminOffices() {
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/offices");
      setOffices(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (o) => {
    setEditing(o);
    setForm({ name: o.name, lat: o.lat, lng: o.lng, radius_meters: o.radius_meters, timezone_offset: o.timezone_offset ?? 7 });
    setDialogOpen(true);
  };
  const useCurrent = () => {
    if (!navigator.geolocation) return toast.error("Geolokasi tidak didukung.");
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setForm((f) => ({ ...f, lat: p.coords.latitude, lng: p.coords.longitude }));
        toast.success("Lokasi diambil dari GPS.");
      },
      (e) => toast.error(e.message || "Gagal ambil lokasi.")
    );
  };
  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, lat: Number(form.lat), lng: Number(form.lng), radius_meters: Number(form.radius_meters), timezone_offset: Number(form.timezone_offset) };
      if (editing) await api.patch(`/admin/offices/${editing.office_id}`, payload);
      else await api.post("/admin/offices", payload);
      toast.success(editing ? "Kantor diperbarui." : "Kantor ditambahkan.");
      setDialogOpen(false); load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Gagal simpan.");
    }
  };
  const doDelete = async (o) => {
    if (!window.confirm(`Hapus kantor "${o.name}"?`)) return;
    try {
      await api.delete(`/admin/offices/${o.office_id}`);
      toast.success("Dihapus.");
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Gagal hapus."); }
  };

  return (
    <div data-testid={TESTIDS.adminOfficesPage} className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-tiny">Manajemen Kantor</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2">Lokasi Kantor</h1>
        </div>
        <button data-testid={TESTIDS.createOfficeBtn} onClick={openCreate}
          className="inline-flex items-center gap-2 bg-klein text-white px-5 py-3 hover:bg-black transition-colors text-sm font-semibold">
          <Building2 className="w-4 h-4" /> Tambah Kantor
        </button>
      </div>

      <div className="border border-black/10 bg-white">
        <table data-testid={TESTIDS.officesTable} className="w-full text-sm">
          <thead className="bg-black text-white text-left">
            <tr><Th>Nama</Th><Th>Zona Waktu</Th><Th>Latitude</Th><Th>Longitude</Th><Th>Radius</Th><Th>Aksi</Th></tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="p-8 text-center text-gray-500">Memuat...</td></tr>}
            {!loading && offices.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-gray-500">Belum ada kantor. Tambahkan minimal 1.</td></tr>}
            {!loading && offices.map((o) => (
              <tr key={o.office_id} className="border-t border-black/10 hover:bg-gray-50">
                <Td className="font-semibold">{o.name}</Td>
                <Td className="text-xs font-mono">{TZ_LABELS[o.timezone_offset ?? 7] || `UTC+${o.timezone_offset}`}</Td>
                <Td className="font-mono">{o.lat}</Td>
                <Td className="font-mono">{o.lng}</Td>
                <Td className="font-mono">{o.radius_meters} m</Td>
                <Td>
                  <div className="flex items-center gap-1">
                    <button onClick={() => openEdit(o)} className="border border-black/20 hover:bg-black hover:text-white p-1.5"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => doDelete(o)} className="border border-black/20 hover:bg-signal-late hover:text-white p-1.5"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-none max-w-xl border-black/20">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit Kantor" : "Tambah Kantor"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <Field label="Nama Kantor">
              <input data-testid={TESTIDS.officeFormName} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="input" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Latitude">
                <input data-testid={TESTIDS.officeFormLat} type="number" step="0.000001" value={form.lat}
                  onChange={(e) => setForm({ ...form, lat: e.target.value })} required className="input font-mono" />
              </Field>
              <Field label="Longitude">
                <input data-testid={TESTIDS.officeFormLng} type="number" step="0.000001" value={form.lng}
                  onChange={(e) => setForm({ ...form, lng: e.target.value })} required className="input font-mono" />
              </Field>
            </div>
            <button data-testid={TESTIDS.officeFormUseCurrent} type="button" onClick={useCurrent}
              className="inline-flex items-center gap-2 border border-black/40 px-4 py-2 hover:bg-black hover:text-white transition-colors text-sm">
              <MapPin className="w-4 h-4" /> Gunakan Lokasi Saya
            </button>
            <Field label="Radius (meter)">
              <input data-testid={TESTIDS.officeFormRadius} type="number" min={10} value={form.radius_meters}
                onChange={(e) => setForm({ ...form, radius_meters: e.target.value })} required className="input" />
            </Field>
            <Field label="Zona Waktu">
              <select data-testid="office-form-timezone"
                value={form.timezone_offset}
                onChange={(e) => setForm({ ...form, timezone_offset: e.target.value })}
                className="input">
                <option value={7}>WIB — UTC+7</option>
                <option value={8}>WITA — UTC+8</option>
                <option value={9}>WIT — UTC+9</option>
              </select>
            </Field>
            <DialogFooter>
              <button type="button" onClick={() => setDialogOpen(false)} className="border border-black/20 px-4 py-2 hover:bg-gray-100 text-sm">Batal</button>
              <button data-testid={TESTIDS.officeFormSubmit} type="submit" className="bg-klein text-white px-5 py-2 hover:bg-black text-sm font-semibold">Simpan</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <style>{`.input { width:100%; border: 1px solid rgba(0,0,0,0.2); padding: 10px 12px; background: white; font-size: 14px; }`}</style>
    </div>
  );
}
function Field({ label, children }) {
  return <div><div className="label-tiny mb-2">{label}</div>{children}</div>;
}
const Th = ({ children }) => <th className="px-4 py-3 text-xs uppercase tracking-widest font-bold">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
